import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import type { JobExecutionStatus } from '@cdw-ae/common-data';

/**
 * Stuck-job banner — surfaces above the device search grid when the
 * `selectStuckJobsForGridSession` selector emits a non-empty list AND
 * reconciliation against the SQL terminal-state confirms the job is still
 * in flight (i.e., not just a missed Firestore listener event).
 *
 * Single action: "Cancel and unlock." Click dispatches
 * `JobsStatusActions.cancelJobRequested` upstream — the device-search
 * component owns the dispatch (this component is a pure presentational
 * standalone with one Output).
 *
 * Decision recap from /grill-me:
 *   - Banner has ONE button (cancel only). No "Keep waiting" — users who
 *     want to wait can simply ignore the banner; the banner stays until
 *     either the job terminates or they cancel.
 *   - Cancel is OPTIMISTIC at the consumer side (lock releases instantly,
 *     API fires in background). This component only emits the click; it
 *     doesn't manage state.
 *   - Renders only when `job` is non-null. When the parent sets it null
 *     (no stuck jobs), the *ngIf in the template removes the banner.
 */
@Component({
  selector: 'app-stuck-job-banner',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      *ngIf="job"
      class="stuck-job-banner"
      role="alert"
      aria-live="polite"
    >
      <mat-icon class="stuck-job-banner__icon" aria-hidden="true">
        warning
      </mat-icon>
      <div class="stuck-job-banner__text">
        <strong>This job has been running longer than expected.</strong>
        <span class="stuck-job-banner__detail">
          You can keep waiting, or cancel to unlock the selection.
        </span>
      </div>
      <button
        mat-flat-button
        color="warn"
        type="button"
        class="stuck-job-banner__action"
        (click)="onCancelClick()"
      >
        Cancel and unlock
      </button>
    </div>
  `,
  styles: [
    `
      .stuck-job-banner {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;
        /* amber-50 background + amber-500 border for warn-tier alert */
        background: #fff7e6;
        border: 1px solid #f5a524;
        border-radius: 4px;
        margin: 0 0 8px 0;
      }

      .stuck-job-banner__icon {
        /* amber-700 — accessible contrast on the amber-50 background */
        color: #b54708;
        flex-shrink: 0;
      }

      .stuck-job-banner__text {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        line-height: 1.3;
      }

      .stuck-job-banner__detail {
        font-size: 0.875rem;
        color: #4a3a1a;
      }

      .stuck-job-banner__action {
        flex-shrink: 0;
      }
    `,
  ],
})
export class StuckJobBannerComponent {
  /**
   * The stuck job to display. When null, the banner does not render.
   * The device-search consumer binds this from the
   * `selectStuckJobsForGridSession` selector (taking the first entry —
   * concurrent stuck jobs in one grid session shouldn't happen because
   * the lock prevents new bulk actions while one is in flight).
   */
  @Input() job: JobExecutionStatus | null = null;

  /**
   * Emits the jobId when the user clicks "Cancel and unlock." Parent
   * dispatches `JobsStatusActions.cancelJobRequested` with this jobId
   * plus customerId and gridSessionUuid (sourced from its own context).
   */
  @Output() cancelClicked = new EventEmitter<number>();

  protected onCancelClick(): void {
    if (this.job) {
      this.cancelClicked.emit(this.job.jobId);
    }
  }
}
